import { enableProdMode, importProvidersFrom } from '@angular/core';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from '@aws-amplify/auth';
import { bootstrapApplication } from '@angular/platform-browser';
import { LicenseManager } from 'ag-grid-enterprise';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withPreloading, withRouterConfig } from '@angular/router';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';
import { routes, PreloadSelectedModulesList } from './app/app.routes';
import { RouterSerializer } from './app/shared/serializers';
import { appReducers } from './app/shared/state/reducers';
import { appMetaReducers } from './app/shared/state/meta-reducers';
import { CacheInterceptor, ErrorInterceptor } from './app/shared/interceptors';
import { AuthModule } from './app/features/auth';
import { SessionEffects } from './app/features/auth-amplify/state/effects/session.effects';
import { AuthTokenService } from './app/features/auth/services';
import { MainContentModule } from './app/components/main-content';
import { OverlayModule } from '@angular/cdk/overlay';
import { CurrencyRatesModule } from './app/features/currency-rates';
import { ReactiveFormsModule } from '@angular/forms';
import { NewsFeatureModule } from './app/features/news/news.module';

const PROD_URL = 'app.fundre.co';
const AG_GRID_LICENSE =
  'Using_this_{AG_Grid}_Enterprise_key_{AG-080895}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{FundRE_Limited}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{FundRE_Limited}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{FundRE_Limited}_need_to_be_licensed___{FundRE_Limited}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{15_September_2026}____[v3]_[01]_MTc4OTQyNjgwMDAwMA==ed8f05ba5d0086fe69940fa78e89e140';

if (environment.isProdMode) {
  enableProdMode();
}

// Load Plausible analytics only for the real production host
if (window.location.host === PROD_URL) {
  const script = document.createElement('script');
  script.defer = true;
  script.dataset.domain = PROD_URL;
  script.src = 'https://plausible.io/js/plausible.js';
  document.head.appendChild(script);
}

LicenseManager.setLicenseKey(AG_GRID_LICENSE);

// Root-level Amplify configuration (pre-lazy-load) with idempotent guard
// so that Auth category is ready before any login flow.
const AMPLIFY_CONFIG_FLAG = '__AMPLIFY_CONFIGURED__';
declare global {
  interface Window {
    [key: string]: any;
  }
}
if (!window[AMPLIFY_CONFIG_FLAG]) {
  // eslint-disable-next-line no-console
  console.log('[root] Configuring Amplify at bootstrap');
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: environment.awsAmplifyConfig.userPoolId,
        userPoolClientId: environment.awsAmplifyConfig.userPoolWebClientId,
        // region omitted earlier due to type narrowing; cast to allow while keeping runtime correctness
        region: environment.awsAmplifyConfig.region,
      } as any,
    },
  });
  window[AMPLIFY_CONFIG_FLAG] = true;
  // After configure attempt to eagerly fetch session & cache idToken (non-blocking)
  async function primeAmplifySession(): Promise<void> {
    try {
      const session: any = await fetchAuthSession();
      const token = session?.tokens?.idToken?.toString();
      if (token) {
        // Delay injection until DI is ready by scheduling microtask after bootstrap
        queueMicrotask(() => {
          try {
            const injector = (window as any).ng?.getInjector?.(document.querySelector('app-root'));
            const authTokenService: AuthTokenService | undefined = injector?.get?.(AuthTokenService);
            authTokenService?.cacheToken(token);
            // eslint-disable-next-line no-console
            console.log('[root] Primed idToken cache on bootstrap');
          } catch {
            // swallow (best-effort)
          }
        });
      }
    } catch (e) {
      // Best-effort prime: ignore failures in production, log quietly in non-prod
      if (!environment.isProdMode) {
        // eslint-disable-next-line no-console
        console.debug('[root] Skipping idToken prime (non-fatal):', e);
      }
    }
  }
  // Top-level await not enabled (module: es2020). Using an async function call instead.
  // NOSONAR: Prefer top-level await suggestion (S6671) is not applicable under current TS module settings.
  primeAmplifySession();
}

async function bootstrapApp(): Promise<void> {
  try {
    await bootstrapApplication(AppComponent, {
      providers: [
        provideRouter(
          routes,
          withPreloading(PreloadSelectedModulesList),
          withRouterConfig({ paramsInheritanceStrategy: 'always' })
        ),
        PreloadSelectedModulesList,
        provideHttpClient(withInterceptorsFromDi()),
        { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        provideAnimations(),
        importProvidersFrom(
          StoreModule.forRoot(appReducers, {
            metaReducers: appMetaReducers,
            runtimeChecks: {
              strictStateImmutability: !environment.isProdMode,
              strictActionImmutability: !environment.isProdMode,
              strictStateSerializability: !environment.isProdMode,
              strictActionWithinNgZone: !environment.isProdMode,
              strictActionTypeUniqueness: !environment.isProdMode,
              strictActionSerializability: false,
            },
          }),
          EffectsModule.forRoot([SessionEffects]),
          StoreRouterConnectingModule.forRoot({ serializer: RouterSerializer }),
          AuthModule.forRoot(),
          MainContentModule,
          OverlayModule,
          CurrencyRatesModule,
          ReactiveFormsModule,
          NewsFeatureModule
        ),
      ],
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }
}
// Top-level await not enabled (module: es2020). Using an async function call instead.
// NOSONAR: Prefer top-level await suggestion (S6671) is not applicable under current TS module settings.
bootstrapApp();
